import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, rp, CUSTOMER_TYPES, OVERRIDE_REASONS, DISCOUNT_REASONS, BOOKING_STATUSES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const times = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);
const L = ({ label, children }) => (
  <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</span><div className="mt-1">{children}</div></label>
);
const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

export default function NewBooking() {
  const nav = useNavigate();
  const [pkgs, setPkgs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [addons, setAddons] = useState([]);
  const [reasons, setReasons] = useState(DISCOUNT_REASONS);
  const [avail, setAvail] = useState(null);
  const [quote, setQuote] = useState(null);
  const [sources, setSources] = useState([]);
  const [f, setF] = useState({
    rental_date: new Date().toISOString().slice(0, 10), start_time: "14:00", package_id: "", quantity: 1,
    delivery_type: "delivery", delivery_area_id: "", recipient_name: "", address: "", area: "", district: "",
    city: "Jakarta", postal_code: "", maps_link: "", location_notes: "", addon_ids: [], promo_code: "",
    vehicle_type: "car", pickup_fee_override: "",
    customer_name: "", customer_whatsapp: "", customer_instagram: "", customer_email: "", customer_type: "Personal", source: "",
    notes: "", price_override: "", override_reason: "", manual_discount: 0, manual_discount_reason: "",
    delivery_fee_override: "", deposit_override: "", booking_status: "Confirmed", created_by_admin: true,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/packages?all=1").then((r) => { const act = r.data.filter((p) => p.active); setPkgs(act); if (act[0]) set("package_id", act[0].id); });
    api.get("/delivery-areas?all=1").then((r) => setAreas(r.data));
    api.get("/addons?all=1").then((r) => setAddons(r.data));
    api.get("/booking-sources").then((r) => setSources(r.data));
    api.get("/settings").then((r) => { if (r.data.discount_reasons?.length) setReasons(r.data.discount_reasons); });
  }, []);

  const payload = useMemo(() => ({
    ...f,
    price_override: f.price_override === "" ? null : Number(f.price_override),
    delivery_fee_override: f.delivery_fee_override === "" ? null : Number(f.delivery_fee_override),
    pickup_fee_override: f.pickup_fee_override === "" ? null : Number(f.pickup_fee_override),
    deposit_override: f.deposit_override === "" ? null : Number(f.deposit_override),
    manual_discount: Number(f.manual_discount || 0),
    promo_code: f.promo_code || null,
    customer_name: f.customer_name || "Walk-in",
    customer_whatsapp: f.customer_whatsapp || "0000000000",
  }), [f]);

  useEffect(() => {
    if (!f.package_id) return;
    api.post("/availability", { rental_date: f.rental_date, start_time: f.start_time, package_id: f.package_id, quantity: f.quantity, honor_start_time: true })
      .then((r) => setAvail(r.data)).catch(() => setAvail(null));
    api.post("/quote", payload).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    // eslint-disable-next-line
  }, [f]);

  const submit = async () => {
    if (!f.customer_name || !f.customer_whatsapp) return toast.error("Customer name and WhatsApp are required");
    try {
      const r = await api.post("/bookings", payload);
      toast.success(`Booking ${r.data.booking_code} created`);
      nav(`/admin/bookings/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-new-booking">
      <h1 className="font-display text-3xl tracking-tight">New Booking</h1>
      <div className="mt-6 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
        <div className="space-y-6">
          <section className="bg-white border border-black/10 rounded-lg p-5 grid sm:grid-cols-2 gap-4">
            <L label="Rental Date"><Input data-testid="nb-date" type="date" value={f.rental_date} onChange={(e) => set("rental_date", e.target.value)} /></L>
            <L label="Start Time"><select data-testid="nb-time" className={sel} value={f.start_time} onChange={(e) => set("start_time", e.target.value)}>{times.map((x) => <option key={x}>{x}</option>)}</select></L>
            <L label="Package"><select data-testid="nb-package" className={sel} value={f.package_id} onChange={(e) => set("package_id", e.target.value)}>
              {pkgs.map((p) => <option key={p.id} value={p.id}>{p.name} — {rp(p.promo_price || p.normal_price)}</option>)}</select></L>
            <L label="Quantity"><Input data-testid="nb-qty" type="number" min={1} value={f.quantity} onChange={(e) => set("quantity", Number(e.target.value))} /></L>
            {avail && <div className="sm:col-span-2 text-sm" data-testid="nb-availability">
              <span className={avail.ok ? "text-emerald-700" : "text-red-600"}>{avail.available_units} of {avail.total_units} units available</span>
              {!avail.ok && <span className="ml-2 text-red-600">— overbooking will be allowed for admin, please verify.</span>}
            </div>}
          </section>

          <section className="bg-white border border-black/10 rounded-lg p-5 grid sm:grid-cols-2 gap-4">
            <L label="Customer Name"><Input data-testid="nb-name" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></L>
            <L label="WhatsApp"><Input data-testid="nb-wa" value={f.customer_whatsapp} onChange={(e) => set("customer_whatsapp", e.target.value)} /></L>
            <L label="Sumber Booking">
              <select data-testid="nb-source" className={sel} value={f.source} onChange={(e) => set("source", e.target.value)}>
                <option value="">Admin (default)</option>
                {(sources || []).map((s) => <option key={s}>{s}</option>)}
              </select>
            </L>
            <L label="Instagram"><Input value={f.customer_instagram} onChange={(e) => set("customer_instagram", e.target.value)} /></L>
            <L label="Email"><Input value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></L>
            <L label="Customer Type"><select className={sel} value={f.customer_type} onChange={(e) => set("customer_type", e.target.value)}>{CUSTOMER_TYPES.map((c) => <option key={c}>{c}</option>)}</select></L>
            <L label="Booking Status"><select data-testid="nb-status" className={sel} value={f.booking_status} onChange={(e) => set("booking_status", e.target.value)}>{BOOKING_STATUSES.map((c) => <option key={c}>{c}</option>)}</select></L>
          </section>

          <section className="bg-white border border-black/10 rounded-lg p-5 grid sm:grid-cols-2 gap-4">
            <L label="Service Mode"><select data-testid="nb-service-mode" className={sel} value={f.delivery_type} onChange={(e) => set("delivery_type", e.target.value)}>
              <option value="customer_arranged">Delivery — kurir diatur customer (Rp0)</option>
              <option value="delivery">Delivery berbiaya (kasus khusus)</option>
              <option value="self_pickup">Ambil Sendiri (Pickup)</option>
              <option value="venue">Main di Venue</option></select></L>
            <L label="Vehicle"><select data-testid="nb-vehicle" className={sel} value={f.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)}>
              <option value="bike">Bike (Lalamove)</option><option value="car">Car / MPV (Lalamove)</option></select></L>
            <L label="Area"><select data-testid="nb-area" className={sel} value={f.delivery_area_id}
              onChange={(e) => { const a = areas.find((x) => x.id === e.target.value); set("delivery_area_id", e.target.value); set("area", a?.area_name || ""); }}>
              <option value="">—</option>{areas.map((a) => <option key={a.id} value={a.id}>{a.area_name}</option>)}</select></L>
            <div className="sm:col-span-2"><L label="Address"><Textarea rows={2} value={f.address} onChange={(e) => set("address", e.target.value)} /></L></div>
            <L label="Delivery Fee Override"><Input data-testid="nb-delivery-override" type="number" placeholder="auto" value={f.delivery_fee_override} onChange={(e) => set("delivery_fee_override", e.target.value)} /></L>
            <L label="Location Notes"><Input value={f.location_notes} onChange={(e) => set("location_notes", e.target.value)} /></L>
          </section>

          <section className="bg-white border border-black/10 rounded-lg p-5 grid sm:grid-cols-2 gap-4">
            <L label="Manual Price Override (per unit)"><Input data-testid="nb-price-override" type="number" placeholder="package price" value={f.price_override} onChange={(e) => set("price_override", e.target.value)} /></L>
            <L label="Override Reason"><select className={sel} value={f.override_reason} onChange={(e) => set("override_reason", e.target.value)}>
              <option value="">—</option>{OVERRIDE_REASONS.map((r) => <option key={r}>{r}</option>)}</select></L>
            <L label="Manual Discount (Rp)"><Input data-testid="nb-manual-discount" type="number" value={f.manual_discount} onChange={(e) => set("manual_discount", e.target.value)} /></L>
            <L label="Discount Reason"><select className={sel} value={f.manual_discount_reason} onChange={(e) => set("manual_discount_reason", e.target.value)}>
              <option value="">—</option>{reasons.map((r) => <option key={r}>{r}</option>)}</select></L>
            <L label="Promo Code"><Input value={f.promo_code} onChange={(e) => set("promo_code", e.target.value.toUpperCase())} /></L>
            <L label="Deposit Override"><Input data-testid="nb-deposit-override" type="number" placeholder="auto" value={f.deposit_override} onChange={(e) => set("deposit_override", e.target.value)} /></L>
            <div className="sm:col-span-2"><L label="Add-ons">
              <div className="flex flex-wrap gap-2">{addons.map((a) => {
                const on = f.addon_ids.includes(a.id);
                return <button key={a.id} onClick={() => set("addon_ids", on ? f.addon_ids.filter((x) => x !== a.id) : [...f.addon_ids, a.id])}
                  className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-ink text-cream border-ink" : "border-black/15"}`}>{a.name} · {rp(a.price)}</button>;
              })}</div></L></div>
            <div className="sm:col-span-2"><L label="Internal Notes"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></L></div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-6 bg-white border border-black/10 rounded-lg p-5 text-sm space-y-1.5" data-testid="nb-summary">
          <div className="text-[11px] uppercase tracking-widest text-ink-soft mb-2">Summary</div>
          <Row l="Rental" v={rp(quote?.rental_subtotal || 0)} />
          {quote?.addon_total > 0 && <Row l="Add-ons" v={rp(quote.addon_total)} />}
          <Row l="Delivery" v={rp(quote?.delivery_fee || 0)} />
          <Row l="Pickup" v={rp(quote?.pickup_fee || 0)} />
          {quote?.loyalty_discount > 0 && <Row l={`${quote.loyalty_tier} -${quote.loyalty_discount_percent}%`} v={`-${rp(quote.loyalty_discount)}`} />}
          <Row l="Vehicle" v={quote?.vehicle_type === "bike" ? "Bike" : quote?.vehicle_type === "car" ? "Car / MPV" : "Self Pickup"} />
          {quote?.promo_discount > 0 && <Row l={`Promo ${quote.promo_code}`} v={`-${rp(quote.promo_discount)}`} />}
          {quote?.manual_discount > 0 && <Row l="Manual Discount" v={`-${rp(quote.manual_discount)}`} />}
          <div className="h-px bg-black/10 my-2" />
          <Row l="Rental Total" v={rp(quote?.final_rental_total || 0)} bold />
          <Row l="Deposit" v={rp(quote?.deposit_amount || 0)} />
          <Row l="TOTAL" v={rp(quote?.total_payment || 0)} bold />
          <button data-testid="nb-submit" onClick={submit} className="mt-4 w-full lm-btn-primary rounded-md py-3 font-semibold">Create Booking</button>
        </aside>
      </div>
    </div>
  );
}

const Row = ({ l, v, bold }) => (
  <div className="flex justify-between gap-3"><span className="text-ink-soft">{l}</span><span className={bold ? "font-semibold" : ""}>{v}</span></div>
);
